import { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { matchPath, useLocation } from 'react-router-dom';
import { color, Modal, toast, Typography } from '@uniquegood/realworld-studio-design';
import axios, { AxiosResponse } from 'axios';
import { css } from '@emotion/react';
import PricingCard from './components/PricingCard';
import { pricingPlans } from './data/pricing';
import { onMobile } from '@/styles/responsive';
import PricingToggle from '@/pages/pricing/components/pricingToggle';
import { BillingPeriod } from './types';
import { useModalState, usePostRequest, useRequest } from '@/hooks';
import { BILLING_KEY_DOMAIN, PRO_PERIOD_MAP } from '@/constants/pricing';
import { ReactComponent as SpinnerIcon } from '@/assets/pricing/spinner.svg';
import { track, ClickEventName, PromotionTriggerLocation } from '@/track';

export interface PaymentSuccessData {
  appName: string;
  userName: string;
}

interface PromotionModalProps {
  onClose: () => void;
  onPaymentSuccess?: (data: PaymentSuccessData) => void;
  onPaymentFail?: (onRetry: () => void) => void;
  currentPlan?: PlanType;
  recommendedPlan?: Exclude<PlanType, 'Free'>;
  triggerLocation?: PromotionTriggerLocation;
}

export default function PromotionModal({
  onClose,
  onPaymentSuccess,
  onPaymentFail,
  currentPlan,
  recommendedPlan,
  triggerLocation
}: PromotionModalProps) {
  const plans = pricingPlans;
  const location = useLocation();
  const match = matchPath<{ appId: string }>(location.pathname, {
    path: '/apps/:appId',
    exact: false
  });
  const appId = match?.params?.appId;

  const { data: me, mutate: mutateMe } = useRequest<MyInfo>('/api/me');
  const { data: app, mutate: mutateApp } = useRequest<RealWorldApp>(`/apps/${appId}`);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const { modal, openModal, closeModal } = useModalState();
  const { data: applicationInfo, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);

  const { trigger: triggerCheckoutVerify } = usePostRequest<CheckoutVerifyResponse>(
    `/api/applications/${appId}/plans/checkout/verify`
  );

  const { trigger: triggerSubscribe, isLoading: isSubscribing } = usePostRequest<AxiosResponse>(
    `/api/applications/${appId}/plans/subscribe`
  );

  const handleBillingPeriodChange = (period: BillingPeriod) => {
    setBillingPeriod(period);
  };

  const openPaymentPage = useCallback(() => {
    if (!appId || !me) {
      return;
    }

    const accessToken = localStorage.getItem('accessToken');

    window.open(
      `${BILLING_KEY_DOMAIN}/billing?accessToken=${accessToken}&mode=register&customerEmail=${me.email}&customerName=${me.name}&applicationId=${appId}&userId=${me.id}`,
      'billing-popup',
      'width=800,height=800'
    );
  }, [appId, me]);

  const handlePaymentFail = () => {
    onClose();
    if (onPaymentFail) {
      onPaymentFail(openPaymentPage);
    } else {
      // 하위 호환성: 콜백이 없으면 내부 모달 사용 (동작 안 함 - 언마운트됨)
      openModal({
        title: '프로 요금제 결제에 실패했어요',
        children: (
          <Typography type="body">
            결제 실패가 반복되면 1:1 문의하기 버튼을 통해 문의글을 남겨 주세요.
          </Typography>
        ),
        primaryAction: {
          content: '다시 결제하기',
          onAction: () => {
            openPaymentPage();
            closeModal();
          }
        },
        secondaryAction: {
          content: '1:1 문의하기',
          onAction: () => {
            if (ChannelIO) {
              ChannelIO('showMessenger');
            }
          }
        }
      });
    }
  };

  const handlePaymentSuccess = async () => {
    await mutateMe();
    const updatedApp = await mutateApp();
    await mutateApplicationInfo();
    onClose();
    if (onPaymentSuccess) {
      onPaymentSuccess({
        appName: updatedApp?.name || app?.name || applicationInfo?.data?.name || '',
        userName: me?.name || ''
      });
    } else {
      // 하위 호환성: 콜백이 없으면 내부 모달 사용 (동작 안 함 - 언마운트됨)
      const appName = updatedApp?.name || app?.name || applicationInfo?.data?.name || '';
      openModal({
        title: '프로 요금제 결제가 완료됐어요!',
        children: (
          <>
            <div>
              <Typography
                type="body"
                as="span"
                cssStyle={css`
                  color: #7800ff;
                `}
              >
                {appName}
              </Typography>
              <Typography type="body" as="span">
                에 프로 요금제가 적용됐어요.
              </Typography>
            </div>
            <Typography type="body">{`마음껏 ${me?.name}님의 아이디어를 펼쳐보세요!`}</Typography>
          </>
        ),
        primaryAction: {
          content: '확인',
          onAction: () => {
            closeModal();
          }
        }
      });
    }
  };

  const handleSubscribeResponse = async (response: AxiosResponse) => {
    const { status } = response;

    if (status === 200) {
      await handlePaymentSuccess();
    } else {
      handlePaymentFail();
    }
  };

  const handleCtaClick = async (planId: PlanType) => {
    // Mixpanel 이벤트 추적
    const channelInfo = applicationInfo?.data;

    if (planId === 'Free') {
      track.onClick({
        clickEventName: ClickEventName.click_plan_promotion_modal_free,
        params: {
          channel_id: appId ?? '',
          channel_name: channelInfo?.name ?? '',
          use_purpose_type: channelInfo?.usePurposeType ?? '',
          plan_type: currentPlan ?? '',
          trigger_location: triggerLocation ?? ''
        }
      });

      window.open('/pricing', '_blank', 'noopener,noreferrer');
    } else if (planId === 'Pro') {
      track.onClick({
        clickEventName: ClickEventName.click_plan_promotion_modal_pro,
        params: {
          channel_id: appId ?? '',
          channel_name: channelInfo?.name ?? '',
          use_purpose_type: channelInfo?.usePurposeType ?? '',
          plan_type: currentPlan ?? '',
          trigger_location: triggerLocation ?? ''
        }
      });

      try {
        const response = await triggerCheckoutVerify();

        if (!response) {
          toast({ message: '결제 정보 확인에 실패했어요.', type: 'error' });
          return;
        }

        const hasBillingKey = !response.success;

        if (!hasBillingKey) {
          openPaymentPage();
        }
      } catch (error) {
        let errorTitle = '';

        if (axios.isAxiosError(error)) {
          const response = error.response as
            | { data?: { message?: string; errorTitle?: string } }
            | undefined;
          errorTitle = response?.data?.errorTitle || '';
        } else if (error && typeof error === 'object' && 'errorTitle' in error) {
          errorTitle = (error as unknown as { errorTitle: string }).errorTitle;
        } else {
          errorTitle = '알 수 없는 오류가 발생했습니다.';
        }

        if (errorTitle === 'AlreadyExistsApplicationBillingMethod') {
          const subscribeResponse = await triggerSubscribe({
            productType: PRO_PERIOD_MAP[billingPeriod]
          });

          if (subscribeResponse) {
            handleSubscribeResponse(subscribeResponse);
          }
        }
      }
    } else if (planId === 'Enterprise') {
      track.onClick({
        clickEventName: ClickEventName.click_plan_promotion_modal_enterprise,
        params: {
          channel_id: appId ?? '',
          channel_name: channelInfo?.name ?? '',
          use_purpose_type: channelInfo?.usePurposeType ?? '',
          plan_type: currentPlan ?? '',
          trigger_location: triggerLocation ?? ''
        }
      });

      window.open('/contact', '_blank', 'noopener,noreferrer');
    } else if (planId === 'Class') {
      track.onClick({
        clickEventName: ClickEventName.click_plan_promotion_modal_class,
        params: {
          channel_id: appId ?? '',
          channel_name: channelInfo?.name ?? '',
          use_purpose_type: channelInfo?.usePurposeType ?? '',
          plan_type: currentPlan ?? '',
          trigger_location: triggerLocation ?? ''
        }
      });

      window.open(
        'https://realworld-school.org/theme/realworld/subscribe/',
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleBillingMessage = async (event: MessageEvent) => {
    // 보안: origin 검증 (결제 페이지 도메인만 허용)
    if (event.origin !== BILLING_KEY_DOMAIN) {
      return;
    }

    const { success } = event.data;

    // 빌링키 등록 성공 시
    if (success) {
      // 빌링키로 요금제 구매 API 호출
      try {
        await triggerSubscribe({
          productType: PRO_PERIOD_MAP[billingPeriod]
        });

        await handlePaymentSuccess();
      } catch (error) {
        console.log('구독 처리 오류 error', error);
      }
    } else {
      handlePaymentFail();
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleBillingMessage);

    return () => {
      window.removeEventListener('message', handleBillingMessage);
    };
  }, [billingPeriod]);

  return (
    <div>
      {isSubscribing && (
        <div
          css={css`
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.4);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <SpinnerIcon width="50px" height="50px" css={SpinnerIconStyle} />
        </div>
      )}
      <Modal {...modal} />
      <CloseIconStyle onClick={onClose} aria-label="닫기">
        <img width="20px" height="20px" src="/assets/icons/close-x.svg" alt="close icon" />
      </CloseIconStyle>
      <Title>요금제 혜택을 확인해 보세요!</Title>
      <ToggleWrapper>
        <PricingToggle selected={billingPeriod} onChange={handleBillingPeriodChange} />
      </ToggleWrapper>
      <PlansGrid>
        {plans.map((plan) => (
          <PricingCard
            key={plan.id}
            plan={{
              ...plan,
              badge: recommendedPlan === plan.id ? '추천 요금제' : undefined,
              highlighted: recommendedPlan === plan.id
            }}
            currentPlan={currentPlan}
            billingPeriod={billingPeriod}
            onCtaClick={handleCtaClick}
            ctaDisabled={
              plan.id === currentPlan || (plan.id === 'Pro' && currentPlan === 'Teacher')
            }
          />
        ))}
      </PlansGrid>
    </div>
  );
}

const SpinnerIconStyle = css`
  width: 50px;
  height: 50px;
  flex-shrink: 0;
`;

const CloseIconStyle = styled.button`
  width: 40px;
  height: 40px;
  position: absolute;
  top: 24px;
  right: 24px;
  cursor: pointer;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;

  ${onMobile} {
    top: 26px;
    right: 26px;
  }
`;

const Title = styled.h1`
  width: 1080px;
  font-size: 24px;
  font-weight: 700;
  color: ${color.text_default};
  text-align: center;
  margin: 0;
`;

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

const PlansGrid = styled.div`
  display: flex;
  gap: 16px;
  margin: 40px auto 0 auto;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;
